// CSRF "double submit" support for cookie-authenticated requests.
//
// The backend authenticates from the HttpOnly `pms_session` cookie. Because a
// browser attaches that cookie to cross-site requests too, every state-changing
// request must also echo the readable `XSRF-TOKEN` cookie back in the
// `X-XSRF-TOKEN` header. A forged cross-site request cannot read that cookie, so
// it cannot produce a matching header and the server answers 419.

export const CSRF_COOKIE = "XSRF-TOKEN";
export const CSRF_HEADER = "X-XSRF-TOKEN";
export const CSRF_FAILURE_STATUS = 419;
export const CSRF_FAILURE_MESSAGE =
  "Your session is out of date. Please log out and log in again.";

// Value of the XSRF-TOKEN cookie, or null when it is not set (not logged in, or
// a session that predates CSRF protection).
export const getXsrfToken = () => {
  if (typeof document === "undefined") return null;

  const prefix = `${CSRF_COOKIE}=`;
  const entry = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  if (!entry) return null;

  const raw = entry.slice(prefix.length);
  try {
    return decodeURIComponent(raw) || null;
  } catch {
    return raw || null;
  }
};

// Adds the CSRF header to a Headers / AxiosHeaders instance when a token exists.
export const applyCsrfHeader = (headers) => {
  const token = getXsrfToken();
  if (token) headers.set(CSRF_HEADER, token);
  return headers;
};

// The server's 419 text names an HTTP header, which means nothing to users.
// Swap in a message they can act on; callers that show `data.message` keep working.
export const withFriendlyCsrfMessage = (data) => ({
  ...(data && typeof data === "object" ? data : {}),
  message: CSRF_FAILURE_MESSAGE,
});

// Wraps an RTK Query base query so a 419 carries the friendly message.
export const withCsrfFailureMessage = (baseQuery) => async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);
  if (result.error?.status === CSRF_FAILURE_STATUS) {
    return { ...result, error: { ...result.error, data: withFriendlyCsrfMessage(result.error.data) } };
  }
  return result;
};
